import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * PartnerPhoneDemo — visual centrepiece of the ITLY HUB page.
 * Four tabs walk the partner through the operator app experience:
 *   1. Incoming booking request (notification-style)
 *   2. Confirm / decline (action screen)
 *   3. QR check-in (scanner mock)
 *   4. Monthly statement (with €0 no-show highlighted)
 * Each tab has a left-column description (title + body) and a right-column
 * mock iPhone-style screen. Users can click tabs to see the flow.
 */
export default function PartnerPhoneDemo() {
    const { t } = useI18n();
    const [tab, setTab] = useState(1);

    const tabs = [1, 2, 3, 4];

    return (
        <section
            id="app-preview"
            data-testid="partner-phone-demo"
            data-reveal="up"
            style={{
                padding: "96px 32px 88px",
                background: "var(--itly-page)",
                position: "relative",
                overflow: "hidden",
            }}
        >
            <div style={{ maxWidth: 1160, margin: "0 auto" }}>
                {/* Header */}
                <div style={{ textAlign: "center", marginBottom: 44 }}>
                    <div className="itly-badge">{t("phoneDemo.badge")}</div>
                    <h2
                        style={{
                            fontFamily: "Fraunces, serif",
                            fontSize: "clamp(32px, 4vw, 52px)",
                            lineHeight: 1.05,
                            color: "var(--itly-fg-strong)",
                            marginTop: 20,
                            marginBottom: 18,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        {t("phoneDemo.title_a")}
                        <br />
                        <em style={{ color: "var(--itly-orange-500)", fontStyle: "italic" }}>
                            {t("phoneDemo.title_b")}
                        </em>
                    </h2>
                    <p
                        style={{
                            maxWidth: 720,
                            margin: "0 auto",
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: 1.65,
                            color: "var(--itly-muted)",
                        }}
                    >
                        {t("phoneDemo.sub")}
                    </p>
                </div>

                {/* Tab bar */}
                <div
                    className="itly-phone-tabs"
                    role="tablist"
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 10,
                        marginBottom: 36,
                    }}
                >
                    {tabs.map((n) => {
                        const active = n === tab;
                        return (
                            <button
                                key={n}
                                data-testid={`phone-tab-${n}`}
                                onClick={() => setTab(n)}
                                role="tab"
                                aria-selected={active}
                                style={{
                                    padding: "10px 18px",
                                    background: active ? "var(--itly-teal-900)" : "var(--itly-surface)",
                                    color: active ? "#fff" : "var(--itly-fg-strong)",
                                    border: `1px solid ${active ? "var(--itly-teal-900)" : "var(--itly-border)"}`,
                                    borderRadius: 999,
                                    fontFamily: "Inter, sans-serif",
                                    fontSize: 13.5,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition: "background 180ms ease, color 180ms ease",
                                }}
                            >
                                <span style={{ opacity: 0.55, marginRight: 6 }}>0{n}</span>
                                {t(`phoneDemo.t${n}.label`)}
                            </button>
                        );
                    })}
                </div>

                {/* Content area — text left, phone mock right */}
                <div
                    className="itly-phone-shell"
                    style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 36,
                        alignItems: "center",
                    }}
                >
                    <div>
                        <h3
                            style={{
                                fontFamily: "Fraunces, serif",
                                fontSize: 28,
                                lineHeight: 1.15,
                                color: "var(--itly-fg-strong)",
                                margin: "0 0 14px",
                                letterSpacing: "-0.005em",
                            }}
                            data-testid={`phone-tab-${tab}-title`}
                        >
                            {t(`phoneDemo.t${tab}.title`)}
                        </h3>
                        <p
                            style={{
                                fontFamily: "Inter, sans-serif",
                                fontSize: 15.5,
                                lineHeight: 1.7,
                                color: "var(--itly-muted)",
                                margin: 0,
                            }}
                        >
                            {t(`phoneDemo.t${tab}.body`)}
                        </p>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        <PhoneFrame tab={tab} t={t} />
                    </div>
                </div>

                <div
                    style={{
                        marginTop: 28,
                        textAlign: "center",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 12,
                        color: "var(--itly-muted)",
                        fontStyle: "italic",
                    }}
                >
                    {t("phoneDemo.disclaimer")}
                </div>
            </div>
        </section>
    );
}

function PhoneFrame({ tab, t }) {
    return (
        <div
            style={{
                width: 300,
                height: 590,
                background: "#0F1516",
                borderRadius: 42,
                padding: 10,
                boxShadow: "0 24px 60px rgba(15, 61, 62, 0.22)",
                position: "relative",
            }}
            aria-hidden="true"
        >
            {/* Notch */}
            <div
                style={{
                    position: "absolute",
                    top: 18,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 110,
                    height: 24,
                    borderRadius: 999,
                    background: "#0F1516",
                    zIndex: 2,
                }}
            />
            <div
                style={{
                    background: "var(--itly-cream)",
                    borderRadius: 32,
                    height: "100%",
                    overflow: "hidden",
                    position: "relative",
                    padding: "44px 18px 20px",
                }}
            >
                {tab === 1 && <ScreenBookingRequest t={t} />}
                {tab === 2 && <ScreenConfirm t={t} />}
                {tab === 3 && <ScreenQRScan t={t} />}
                {tab === 4 && <ScreenStatement t={t} />}
            </div>
        </div>
    );
}

function ScreenBookingRequest({ t }) {
    return (
        <div>
            <div style={{ marginBottom: 14, textAlign: "center" }}>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--itly-muted)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>ITLY HUB</div>
            </div>
            <div
                style={{
                    background: "var(--itly-surface)",
                    borderRadius: 18,
                    padding: "16px 16px 18px",
                    boxShadow: "0 8px 22px rgba(15, 61, 62, 0.09)",
                    border: "1px solid var(--itly-border)",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                    <span aria-hidden="true" style={{ width: 32, height: 32, borderRadius: 10, background: "var(--itly-orange-500)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "Fraunces, serif", fontWeight: 600, fontSize: 15 }}>◉</span>
                    <div>
                        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 700, color: "var(--itly-fg-strong)", lineHeight: 1.2 }}>{t("phoneDemo.t1.notifTitle")}</div>
                        <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--itly-muted)", marginTop: 2 }}>{t("phoneDemo.t1.notifSub")}</div>
                    </div>
                </div>
                <div style={{ height: 1, background: "var(--itly-border)", margin: "10px 0 14px" }} />
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 15, fontWeight: 700, color: "var(--itly-fg-strong)", marginBottom: 4 }}>{t("phoneDemo.t1.mockGuest")}</div>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-fg-strong)", marginBottom: 12 }}>{t("phoneDemo.t1.mockWhen")}</div>
                <div style={{ background: "var(--itly-cream)", borderRadius: 10, padding: "10px 12px", fontFamily: "Inter, sans-serif", fontSize: 12, fontStyle: "italic", color: "var(--itly-muted)", lineHeight: 1.45 }}>
                    &ldquo;{t("phoneDemo.t1.mockNote")}&rdquo;
                </div>
            </div>
        </div>
    );
}

function ScreenConfirm({ t }) {
    return (
        <div>
            <div style={{ marginBottom: 12, textAlign: "center" }}>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--itly-muted)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>ITLY HUB</div>
            </div>
            <div
                style={{
                    background: "var(--itly-surface)",
                    borderRadius: 18,
                    padding: "16px 16px 18px",
                    boxShadow: "0 8px 22px rgba(15, 61, 62, 0.09)",
                    border: "1px solid var(--itly-border)",
                }}
            >
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 15, fontWeight: 700, color: "var(--itly-fg-strong)", marginBottom: 4 }}>{t("phoneDemo.t1.mockGuest")}</div>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-fg-strong)", marginBottom: 14 }}>{t("phoneDemo.t1.mockWhen")}</div>
                <button style={{ width: "100%", padding: "12px 14px", background: "var(--itly-green-ink)", color: "#fff", border: "none", borderRadius: 10, fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 600, marginBottom: 8, textAlign: "left" }}>
                    ✓ {t("phoneDemo.t2.confirm")}
                </button>
                <button style={{ width: "100%", padding: "12px 14px", background: "var(--itly-surface)", color: "var(--itly-fg-strong)", border: "1px solid var(--itly-border)", borderRadius: 10, fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500, marginBottom: 8, textAlign: "left" }}>
                    ↻ {t("phoneDemo.t2.reschedule")}
                </button>
                <button style={{ width: "100%", padding: "12px 14px", background: "var(--itly-surface)", color: "#B3654A", border: "1px solid var(--itly-border)", borderRadius: 10, fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500, textAlign: "left" }}>
                    ✕ {t("phoneDemo.t2.decline")}
                </button>
            </div>
            <div style={{ marginTop: 12, textAlign: "center", background: "var(--itly-surface-green)", padding: "8px 12px", borderRadius: 999, fontFamily: "Inter, sans-serif", fontSize: 11.5, color: "var(--itly-green-ink)", fontWeight: 600 }}>
                {t("phoneDemo.t2.confirmedStatus")}
            </div>
        </div>
    );
}

function ScreenQRScan({ t }) {
    return (
        <div>
            <div style={{ marginBottom: 14, textAlign: "center" }}>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--itly-muted)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>{t("phoneDemo.t3.scanLabel")}</div>
            </div>
            <div
                style={{
                    background: "var(--itly-surface)",
                    borderRadius: 18,
                    padding: 16,
                    boxShadow: "0 8px 22px rgba(15, 61, 62, 0.09)",
                    border: "1px solid var(--itly-border)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                }}
            >
                <div style={{ width: 170, height: 170, background: "var(--itly-teal-900)", borderRadius: 14, padding: 16, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    <QRPattern />
                </div>
                <div style={{ marginTop: 12, fontFamily: "Inter, sans-serif", fontSize: 12.5, color: "var(--itly-fg-strong)", fontWeight: 600, textAlign: "center" }}>
                    {t("phoneDemo.t3.guest")}
                </div>
                <div style={{ marginTop: 12, background: "var(--itly-surface-green)", padding: "8px 14px", borderRadius: 999, fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--itly-green-ink)", fontWeight: 700 }}>
                    {t("phoneDemo.t3.scanOk")}
                </div>
            </div>
        </div>
    );
}

function QRPattern() {
    // A stylised, non-scannable QR grid built from CSS blocks.
    const cells = new Array(64).fill(0).map((_, i) => (i * 37) % 5 !== 0);
    return (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 3, width: "100%", height: "100%" }}>
            {/* Static decorative grid, never reordered: index keys are safe here */}
            {cells.map((filled, i) => (
                <div
                    key={`qr-cell-${i}`}
                    style={{
                        background: filled ? "#ffffff" : "transparent",
                        borderRadius: 2,
                    }}
                />
            ))}
        </div>
    );
}

function ScreenStatement({ t }) {
    return (
        <div>
            <div style={{ marginBottom: 12, textAlign: "center" }}>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--itly-muted)", letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 600 }}>{t("phoneDemo.t4.header")}</div>
            </div>
            <div
                style={{
                    background: "var(--itly-surface)",
                    borderRadius: 18,
                    padding: "14px 14px 16px",
                    boxShadow: "0 8px 22px rgba(15, 61, 62, 0.09)",
                    border: "1px solid var(--itly-border)",
                }}
            >
                <StatementRow left={t("phoneDemo.t4.row1a")} right={t("phoneDemo.t4.row1b")} />
                <StatementRow left={t("phoneDemo.t4.row2a")} right={t("phoneDemo.t4.row2b")} />
                <StatementRow left={t("phoneDemo.t4.row3a")} right={t("phoneDemo.t4.row3b")} accent="#B3654A" />
                <div style={{ height: 1, background: "var(--itly-border)", margin: "10px 0 12px" }} />
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10.5, color: "var(--itly-muted)", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600, marginBottom: 4 }}>
                    {t("phoneDemo.t4.totalLabel")}
                </div>
                <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, color: "var(--itly-fg-strong)" }}>
                    {t("phoneDemo.t4.totalValue")}
                </div>
                <div style={{ marginTop: 10, background: "var(--itly-peach-050)", padding: "8px 10px", borderRadius: 10 }}>
                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 10.5, color: "var(--itly-orange-700)", fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" }}>
                        {t("phoneDemo.t4.feeLabel")}
                    </div>
                    <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, color: "var(--itly-fg-strong)", marginTop: 2 }}>{t("phoneDemo.t4.feeValue")}</div>
                </div>
                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", background: "#EDF6E4", padding: "8px 10px", borderRadius: 10 }}>
                    <div style={{ fontFamily: "Inter, sans-serif", fontSize: 11, color: "var(--itly-green-ink)", fontWeight: 600 }}>{t("phoneDemo.t4.noshowRow")}</div>
                    <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, color: "var(--itly-orange-500)", fontWeight: 700 }}>{t("phoneDemo.t4.noshowValue")}</div>
                </div>
            </div>
        </div>
    );
}

function StatementRow({ left, right, accent }) {
    return (
        <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", gap: 8 }}>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--itly-fg-strong)" }}>{left}</div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: accent || "var(--itly-muted)", fontWeight: 600, whiteSpace: "nowrap" }}>{right}</div>
        </div>
    );
}
